import React, { useState, useEffect } from 'react';
import adminConsoleService from '@services/adminConsoleService';
import { useTenant } from '@contexts/TenantContext';

export const AuthenticationTab: React.FC = () =>
{
  const { tenant } = useTenant();
  const tenantId = tenant?.Id ?? 0;

  const [enabled, setEnabled] = useState(false);
  const [host, setHost] = useState('');
  const [iKey, setIKey] = useState('');
  const [sKey, setSKey] = useState('');
  const [aKey, setAKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() =>
  {
    adminConsoleService
      .getTwoFactorAuth(tenantId)
      .then(data =>
      {
        setEnabled(data.enableTwoFactorAuth === 'true');
        setHost(data.host ?? '');
        setIKey(data.iKey ?? '');
        setSKey(data.sKey ?? '');
        setAKey(data.aKey ?? '');
      })
      .catch(() => setError('Failed to load two-factor authentication settings.'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () =>
  {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try
    {
      await adminConsoleService.saveTwoFactorAuth({
        TenantId: tenantId,
        EnableTwoFactorAuth: String(enabled),
        DUO_host: host.trim(),
        DUO_ikey: iKey.trim(),
        DUO_skey: sKey.trim(),
        DUO_akey: aKey.trim(),
      });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    }
    catch
    {
      setError('Failed to save settings. Please try again.');
    }
    finally
    {
      setSaving(false);
    }
  };

  if (loading)
  {
    return (
      <div className="flex items-center justify-center h-full">
        <span className="material-icons animate-spin text-[#62aacb] text-[32px]">refresh</span>
      </div>
    );
  }

  return (
    <div className="px-6 py-5 max-w-[560px]">
      <h3 className="text-[16px] font-normal text-[#5697b3] mb-5">Config Two Factor Auth</h3>

      {error && (
        <div className="mb-4 px-3 py-2 bg-[#fdecea] dark:bg-[#3a1a1a] border border-[#e57373] dark:border-[#8b4444] rounded text-[12px] text-[#c62828] dark:text-[#e57373] flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="material-icons text-[14px]">error_outline</span>
            {error}
          </div>
          <button onClick={() => setError(null)}>
            <span className="material-icons text-[14px]">close</span>
          </button>
        </div>
      )}

      {success && (
        <div className="mb-4 px-3 py-2 bg-[#e8f5e9] dark:bg-[#1a3a1a] border border-[#81c784] dark:border-[#4a7a4a] rounded text-[12px] text-[#2e7d32] dark:text-[#81c784] flex items-center gap-2">
          <span className="material-icons text-[14px]">check_circle</span>
          Settings saved successfully.
        </div>
      )}

      <div className="bg-[#fafafa] border border-[#e0e0e0] dark:bg-[#2d2f2f] rounded p-5 flex flex-col gap-4">
        {/* Enables */}
        <div className="flex items-center gap-4">
          <label
            htmlFor="auth-enables"
            className="w-[140px] text-[12px] text-[#A5A7AC] dark:text-[#beb7ac] font-medium flex-shrink-0 cursor-pointer"
          >
            Enables
          </label>
          <span className="corgee_com_checkbox">
            <input
              id="auth-enables"
              type="checkbox"
              checked={enabled}
              onChange={e => setEnabled(e.target.checked)}
            />
            <label htmlFor="auth-enables"></label>
          </span>
        </div>

        {/* Host */}
        <div className="flex items-center gap-4">
          <label className="w-[140px] text-[12px] text-[#A5A7AC] dark:text-[#beb7ac] font-medium flex-shrink-0">
            Host
          </label>
          <input
            type="text"
            value={host}
            onChange={e => setHost(e.target.value)}
            placeholder="api-xxxxxxxx.duosecurity.com"
            disabled={!enabled}
            className="flex-1 border border-[#ddd] rounded px-3 py-1.5 text-[12px] outline-none focus:border-[#62aacb]"
          />
        </div>

        {/* Integration Key */}
        <div className="flex items-center gap-4">
          <label className="w-[140px] text-[12px] text-[#A5A7AC] dark:text-[#beb7ac] font-medium flex-shrink-0">
            Integration Key
          </label>
          <input
            type="text"
            value={iKey}
            onChange={e => setIKey(e.target.value)}
            disabled={!enabled}
            className="flex-1 border border-[#ddd] rounded px-3 py-1.5 text-[12px] outline-none focus:border-[#62aacb]"
          />
        </div>

        {/* Secret Key */}
        <div className="flex items-center gap-4">
          <label className="w-[140px] text-[12px] text-[#A5A7AC] dark:text-[#beb7ac] font-medium flex-shrink-0">
            Secret Key
          </label>
          <input
            type="text"
            value={sKey}
            onChange={e => setSKey(e.target.value)}
            disabled={!enabled}
            className="flex-1 border border-[#ddd] rounded px-3 py-1.5 text-[12px] outline-none focus:border-[#62aacb]"
          />
        </div>

        {/* Application Key */}
        <div className="flex items-center gap-4">
          <label className="w-[140px] text-[12px] text-[#A5A7AC] dark:text-[#beb7ac] font-medium flex-shrink-0">
            Application Key
          </label>
          <input
            type="text"
            value={aKey}
            onChange={e => setAKey(e.target.value)}
            disabled={!enabled}
            className="flex-1 border border-[#ddd] rounded px-3 py-1.5 text-[12px] outline-none focus:border-[#62aacb]"
          />
        </div>

        {/* Save */}
        <div className="flex justify-end pt-1">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-1.5 bg-[#62aacb] text-white rounded text-[12px] hover:bg-[#4e99ba] dark:bg-[#3c6c80] dark:hover:brightness-[0.85] transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};
